import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment-duration-format';

//- Material UI / Style
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import { getGlobalClasses } from '../../themes';

//containers
import AuthUserContainer from '../../containers/AuthUser';

//- UI / Form
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import PublishIcon from '@material-ui/icons/Publish';
import CircularProgress from '@material-ui/core/CircularProgress';

import FormDatePicker from '../../components/form/FormDatePicker';
import FormCheckbox from '../../components/form/FormCheckbox';
import UserPicker from '../../components/advancedForm/UserPicker';
import FormComponent from '../../components/FormComponent';
import ClockOutEmployeeDialog from '../../components/dialogs/ClockOutEmployeeDialog';
import UpdateEmployeeTimeShiftDialog from '../../components/dialogs/UpdateEmployeeTimeShiftDialog';
import TimeClockContainer from '../../containers/TimeClock/TimeClock';
import PaycomFtpRecordContainer from '../../containers/TimeClock/PaycomFtpRecord';
import EnhancedTable from '../../components/table/EnhancedTable';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import PaycomExportConfirmation from '../../components/dialogs/PaycomExportConfirmation';
import TimeClockLocationSelect from '../../components/advancedForm/TimeClockLocationSelect';
import CompanySelect from '../../components/advancedForm/CompanySelect';

const styles = (theme) => {
	return {
		...getGlobalClasses(theme),
		submitButton: {
			marginTop: 20,
			fontSize: 16,
			borderRadius: '100px',
			padding: '13px 45px',
			textTransform: 'uppercase',
			marginRight: 10,
		},
		icon: {
			paddingRight: 5,
		},
		oddRow: {
			backgroundColor: '#f9f9f9',
		},
		resultsHere: {
			padding: 30,
			backgroundColor: '#f1f1f1',
			color: '#777',
			borderRadius: 10,
		},
		runningSearch: {
			paddingLeft: 20,
			display: 'flex',
			alignItems: 'center',
			fontSize: 18,
			color: '#666',
		},
		button: {
			marginRight: 5,
		},
		eventsContainer: {
			padding: 20,
		},
		eventContainer: {
			borderTop: '1px solid #eee',
			marginBottom: 3,
			paddingBottom: 3,
		},
		eventNote: {},
		eventDateTime: {
			fontSize: '80%',
			color: '#666',
		},
	};
};

class PaycomExport extends FormComponent {
	constructor(props) {
		super(props);
		this.state = {
			order: 'asc',
			orderBy: 'clockInDateTime',
			isSearching: false,
			isClockOutEmployeeDialogOpen: false,
			isUpdateEmployeeDialogOpen: false,
			timeClockShiftForClockOut: undefined,

			afterDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
			beforeDate: moment().subtract(2, 'days').format('YYYY-MM-DD'),

			refreshId: 1,
		};
	}

	componentDidMount() {
		const { paycomFtpRecords, searchPaycomFtpRecords } = this.props;
		if (_.isUndefined(paycomFtpRecords)) {
			searchPaycomFtpRecords({});
		}
	}

	getApiParams = () => {
		const { accessToken } = this.props;
		const { shiftForUserIds, timeClockLocationId, companyName, afterDate, beforeDate } = this.state;

		let afterDateTime, beforeDateTime;
		if (afterDate) {
			afterDateTime = moment(afterDate).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
		}
		if (beforeDate) {
			beforeDateTime = moment(beforeDate).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
		}

		const params = {
			accessToken,
			shiftForUserIds,
			afterDate,
			beforeDate,
			afterDateTime,
			beforeDateTime,
			isDryRun: true,
		};

		if (timeClockLocationId) {
			params.timeClockLocationIds = [timeClockLocationId];
		}

		if (companyName) {
			params.companyName = companyName;
		}

		return params;
	};

	handleSearch = () => {
		const { timeClockShiftPaycomExport, showErrors } = this.props;

		const formFieldError = {};
		_.values(this.requiredPathMap).forEach(({ path, label }) => {
			if (!this.state[path]) {
				formFieldError[path] = `${label} is required`;
			}
		});
		if (_.keys(formFieldError).length) {
			this.setState({ formFieldError });
			return showErrors(_.values(formFieldError));
		}

		const params = this.getApiParams();
		this.setState({
			isSearching: true,
		});

		timeClockShiftPaycomExport({
			params,
			callback: (err, r) => {
				this.setState({ isSearching: false });
				if (err) {
					return showErrors(_.isArray(err) ? err : [err]);
				}
				this.formatSearchResults({
					searchResult: r,
					searchApiParams: params,
				});
			},
		});
	};

	formatSearchResults = ({ searchResult, searchApiParams }) => {
		const c = _.get(searchResult, 'collections');
		const timeClockShifts = _.get(c, 'timeClockShiftCollection', []);
		const userMap = _.keyBy(_.get(c, 'userCollection', []), 'userId');
		const paycomPunchInfoByTimeClockShiftId = _.get(
			searchResult,
			'paycomPunchInfoByTimeClockShiftId',
			{}
		);
		const paycomExportMessages = _.get(searchResult, 'paycomExportMessages', []);
		const missingPaycomIdShiftIdSet = new Set(
			_.get(searchResult, 'missingPaycomIdTimeClockShiftIds', [])
		);

		const managerEventToDisplay = {
			managerClockIn: 'clocked in employee',
			managerClockOut: 'clocked out employee',
			managerUpdate: 'changed employee shift',
			managerAdd: 'added employee shift',
		};

		const now = moment();
		const timeClockShiftRows = timeClockShifts.map((timeClockShift) => {
			const {
				timeClockShiftId,
				clockInDateTime,
				clockOutDateTime,
				shiftForUserId,
				timeClockShiftEvents,
				auditStatus,
			} = timeClockShift;

			const user = userMap[shiftForUserId] || {
				firstName: 'Missing',
				lastName: '',
			};
			const userDisplay = `${user.firstName} ${user.lastName}`;

			const punchInfo = paycomPunchInfoByTimeClockShiftId[timeClockShiftId] || {};
			const needsClockOutDecision = !!punchInfo.needsClockOutDecision;
			const punchTypesSort = `${punchInfo.clockInPunchType || ''} / ${
				punchInfo.clockOutPunchType || ''
			}`;
			const punchTypesDisplay = (
				<div>
					<div>
						{punchInfo.clockInPunchType || '—'} / {punchInfo.clockOutPunchType || '—'}
					</div>
					{needsClockOutDecision && (
						<div style={{ color: '#E65100', fontSize: '85%' }}>Needs decision</div>
					)}
				</div>
			);
			const clockInMoment = moment(clockInDateTime);
			const clockInDateTimeDisplay = clockInMoment.format('M/D h:mm a');
			const hasClockOut = clockOutDateTime;

			let durationDiff = now.diff(clockInMoment);
			let clockOutDateTimeDisplay = 'Currently Clocked in';
			if (hasClockOut) {
				const clockOutMoment = moment(clockOutDateTime);
				clockOutDateTimeDisplay = clockOutMoment.format('M/D h:mm a');
				durationDiff = clockOutMoment.diff(clockInMoment);
			}
			let durationDisplay = moment.duration(durationDiff).format('d [days], h [hrs], m [min]');

			timeClockShiftEvents.forEach((timeClockShiftEvent) => {
				const { createdByUserId, dateCreated } = timeClockShiftEvent;
				timeClockShiftEvent.user = userMap[createdByUserId] || {
					firstName: 'Missing',
					lastName: '',
				};
				timeClockShiftEvent.dateCreatedFormatted = moment(dateCreated).format('M/D h:mm a');
			});

			const { classes } = this.props;

			const timeClockShiftEventsFormatted = timeClockShiftEvents.map(
				(timeClockShiftEvent, index) => {
					const {
						timeClockShiftEventId,
						user,
						eventType,
						note,
						dateCreatedFormatted,
					} = timeClockShiftEvent;

					const className = index === 0 ? '' : classes.eventContainer;
					if (eventType === 'userClockIn' || eventType === 'userClockOut') {
						const verb = eventType === 'userClockIn' ? 'clocked in' : 'clocked out';
						return (
							<div key={timeClockShiftEventId} className={className}>
								<div style={{ whiteSpace: 'pre-line' }}>
									{user.firstName} {user.lastName} {verb}
								</div>
							</div>
						);
					} else {
						const verb = managerEventToDisplay[eventType] || 'performed unknown action';
						return (
							<div key={timeClockShiftEventId} className={className}>
								<div style={{ whiteSpace: 'pre-line' }}>
									<strong>
										{user.firstName} {user.lastName}
									</strong>{' '}
									{verb}
								</div>
								<div className={classes.eventNote}>Reason : {note}</div>
								<div className={classes.eventDateTime}>{dateCreatedFormatted}</div>
							</div>
						);
					}
				}
			);

			const updateButton = (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Button
						variant={'outlined'}
						className={classes.button}
						onClick={() => this.updateEmployee({ timeClockShift })}
					>
						Update
					</Button>

					{!hasClockOut && (
						<Button
							variant={'outlined'}
							className={classes.button}
							onClick={() => this.clockOutEmployee({ timeClockShift })}
						>
							Clock Out
						</Button>
					)}
				</div>
			);

			return {
				key: timeClockShiftId,
				timeClockShiftId,
				clockInDateTime,
				clockInDateTimeDisplay,
				clockOutDateTime,
				clockOutDateTimeDisplay,
				durationDiff,
				durationDisplay,
				shiftForUserId,
				userDisplay,
				hasClockOut,
				timeClockShiftEvents,
				timeClockShiftEventsFormatted,
				updateButton,
				auditStatus,
				punchTypesSort,
				punchTypesDisplay,
				needsClockOutDecision,
				isMissingPaycomId: missingPaycomIdShiftIdSet.has(timeClockShiftId),
			};
		});

		this.setState({
			timeClockShiftRows,
			paycomExportMessages,
			paycomSearchResult: searchResult,
			searchApiParams,
			savingStatus: 'done',
			isExportDialogOpen: true,
		});
	};

	handleCloseExportDialog = () => {
		this.setState({
			isExportDialogOpen: false,
		});
	};

	clockOutEmployee = ({ timeClockShift }) => {
		this.setState({
			timeClockShiftForClockOut: timeClockShift,
			isClockOutEmployeeDialogOpen: true,
		});
	};
	handleCloseClockOutEmployeeDialog = () => {
		this.setState({
			timeClockShiftForClockOut: null,
			isClockOutEmployeeDialogOpen: false,
		});
	};
	afterClockOutEmployee = () => {
		this.handleSearch();
	};

	updateEmployee = ({ timeClockShift }) => {
		this.setState({
			timeClockShiftForUpdate: timeClockShift,
			isUpdateEmployeeDialogOpen: true,
		});
	};
	handleCloseUpdateEmployeeDialog = () => {
		this.setState({
			timeClockShiftForUpdate: null,
			isUpdateEmployeeDialogOpen: false,
		});
	};

	afterUpdateEmployee = () => {
		this.handleSearch();
	};

	afterPaycomExport = () => {
		this.props.searchPaycomFtpRecords({});
	};

	render() {
		const {
			classes,
			permissions,
			//myTimeClockPermissions,
			//myCanViewUserIds,
			myCanModifyUserIds,
		} = this.props;

		const { isSearching } = this.state;

		if (!permissions || !_.includes(permissions, 'canViewTimeClocks')) {
			return <div>You do not have valid permissions to view this page.</div>;
		}

		//const isRequired = true;
		return (
			<>
				<form>
					<div className={classes.globalMainHeadline}>Time Clock Paycom Export</div>
					<Paper className={classes.globalMainPaper} style={{ minHeight: 500 }}>
						<Grid spacing={2} container>
							<Grid item xs={12} sm={11} md={10} lg={8}>
								<Grid spacing={2} container>
									<UserPicker
										{...this.setForm('shiftForUserIds', 'Employees')}
										helpText={'Leave blank for everyone'}
										userIdsToShow={myCanModifyUserIds}
										includeHidden={true}
										selectMultiple={true}
									/>

									<TimeClockLocationSelect {...this.setForm('timeClockLocationId', 'Location')} />

									<CompanySelect {...this.setForm('companyName', 'Company')} />

									<FormDatePicker
										{...this.setForm('afterDate', 'Clocked In After This Date')}
										helpText={'Will only show shifts not already exported'}
										format={'M/D/YYYY'}
									/>

									<FormDatePicker
										{...this.setForm('beforeDate', 'Clock In Before This Date')}
										helpText={'Will only show shifts not already exported'}
										format={'M/D/YYYY'}
									/>

									<FormCheckbox
										{...this.setForm(
											'skipClockedInShifts',
											'Skip shifts still clocked in',
											false,
											false
										)}
										helpText={
											'Shifts that are still clocked in will be skipped instead of blocking the export. Skipped shifts stay unexported and will be picked up by a future export once the employee has clocked out.'
										}
									/>

									<Grid item xs={12} style={{ padding: '20px 0px' }}>
										<div
											style={{
												padding: 20,
												border: `1px solid #ddd`,
												backgroundColor: '#fcfcfc',
											}}
										>
											It is advised to set the clock in before date (default) to 2 days back to
											ensure all shifts have finished for the day and there aren't any corrections.
											Same-day exports are supported: recent clock-outs and pending shifts will be
											listed for review before anything is exported
										</div>
									</Grid>

									<Grid item xs={12} style={{ padding: '10px 0px' }}>
										<Button
											onClick={this.handleSearch}
											variant="contained"
											color="primary"
											className={classes.submitButton}
										>
											<PublishIcon className={classes.icon} />
											Search then Export
										</Button>

										{isSearching && (
											<div className={classes.runningSearch}>
												<CircularProgress />
												<span style={{ paddingLeft: 10, paddingTop: 10 }}>Searching...</span>
											</div>
										)}
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Paper>
				</form>

				<Paper className={classes.globalMainPaper} style={{ minHeight: 500 }}>
					<div className={classes.globalMainHeadline}>Past Exports</div>
					{this.renderPastExports()}
				</Paper>

				<ClockOutEmployeeDialog
					open={this.state.isClockOutEmployeeDialogOpen}
					onClose={this.handleCloseClockOutEmployeeDialog}
					timeClockShift={this.state.timeClockShiftForClockOut}
					userMap={this.props.userMap}
					afterSave={this.afterClockOutEmployee}
				/>

				<UpdateEmployeeTimeShiftDialog
					open={this.state.isUpdateEmployeeDialogOpen}
					onClose={this.handleCloseUpdateEmployeeDialog}
					timeClockShift={this.state.timeClockShiftForUpdate}
					userMap={this.props.userMap}
					afterSave={this.afterUpdateEmployee}
				/>

				<PaycomExportConfirmation
					open={this.state.isExportDialogOpen}
					onClose={this.handleCloseExportDialog}
					timeClockShiftRows={this.state.timeClockShiftRows}
					paycomExportMessages={this.state.paycomExportMessages}
					paycomSearchResult={this.state.paycomSearchResult}
					searchApiParams={this.state.searchApiParams}
					afterExport={this.afterPaycomExport}
					refreshSearch={this.handleSearch}
					skipClockedInShifts={!!this.state.skipClockedInShifts}
				/>
			</>
		);
	}

	handleSort = (orderBy) => {
		let order = 'desc';
		if (this.state.orderBy === orderBy && this.state.order === 'desc') {
			order = 'asc';
		}
		this.setState({ order: order, orderBy: orderBy });
	};

	renderPastExports = () => {
		const { paycomFtpRecords, classes } = this.props;
		if (!paycomFtpRecords) {
			return <div className={classes.globalFetching}>Fetching..</div>;
		}

		const { userMap, resourceMap } = this.props;

		const columns = [
			{
				label: 'Export Id',
				sortId: 'paycomFtpRecordId',
				displayId: 'paycomFtpRecordId',
			},
			{
				label: 'Date Exported',
				sortId: 'uploadedDateTime',
				displayId: 'uploadedDateTimeFormatted',
			},
			{
				label: 'Company',
				sortId: 'companyName',
				displayId: 'companyNameFormatted',
			},
			{
				label: 'CSV File',
				sortId: 'resourceId',
				isNotSortable: true,
				displayId: 'resourceFormatted',
			},
			{
				label: 'Exported By',
				sortId: 'userFormatted',
				displayId: 'userFormatted',
			},
		];

		paycomFtpRecords.forEach((record) => {
			const { paycomFtpRecordId, uploadedDateTime, csvResourceId, createdByUserId } = record;

			record.key = paycomFtpRecordId;
			const user = userMap[createdByUserId] || {
				firstName: '',
				lastName: 'Missing',
			};

			record.userFormatted = `${user.firstName} ${user.lastName}`;
			record.uploadedDateTimeFormatted = moment(uploadedDateTime).format('M/D/YY h:mm a');
			record.companyNameFormatted = record.companyName || 'All';

			const resource = resourceMap[csvResourceId];
			if (resource) {
				record.resourceFormatted = (
					<a
						href={resource.url}
						target="_blank"
						rel="noreferrer noopener"
						style={{ textDecoration: 'none' }}
					>
						<Button
							variant="outlined"
							color="primary"
							size="small"
							className={classes.downloadButton}
						>
							<DownloadIcon style={{ marginRight: 10 }} />
							Download CSV
						</Button>
					</a>
				);
			} else {
				record.resourceFormatted = `Missing`;
			}
		});

		return (
			<EnhancedTable
				rows={paycomFtpRecords}
				columns={columns}
				isDense={true}
				defaultOrderBy={'uploadedDateTime'}
				defaultOrder={'desc'}
			/>
		);
	};
}

PaycomExport.propTypes = {
	timeClockShiftPaycomExport: PropTypes.func.isRequired,
	userMap: PropTypes.object,
	resourceMap: PropTypes.object,

	searchPaycomFtpRecords: PropTypes.func.isRequired,
	paycomFtpRecords: PropTypes.array,

	showErrors: PropTypes.func.isRequired,
	authUser: PropTypes.object,
	classes: PropTypes.object.isRequired,
};

export default _.flow([
	withStyles(styles),
	AuthUserContainer,
	TimeClockContainer,
	PaycomFtpRecordContainer,
])(PaycomExport);
